import React, { useContext } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  Select,
  MenuItem,
  FormControl,
  TextField,
  makeStyles,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Undo as UndoIcon,
  Edit as EditIcon,
} from "@material-ui/icons";
import {
  grayColor,
  dangerColor,
  whiteColor,
} from "assets/jss/material-dashboard-pro-react.js";
import AuthContext from "connections/auth/AuthProvider.js";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";

const variantSelectorStyle = (theme, clientColor) => ({
  ...customSelectStyle(theme, clientColor),
  variantCard: {
    border: `1px solid ${grayColor[5]}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    textAlign: "center",
    width: 180,
    height: 120,
    minWidth: 160,
    minHeight: 120,
    maxWidth: 180,
    maxHeight: 120,
    flex: "0 0 auto",
    position: "relative",
    transition: "all 0.3s ease-in-out",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  deletedCard: {
    backgroundColor: grayColor[12],
    borderColor: grayColor[6],
    opacity: 0.6,
  },
  invalidCard: {
    borderColor: dangerColor[0],
    borderWidth: 2,
  },
  variantName: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  editableVariantName: {
    position: "relative",
    "& .MuiInputBase-root": {
      backgroundColor: "rgba(0, 0, 0, 0.02)",
      borderRadius: theme.shape.borderRadius,
      border: "1px dashed rgba(0, 0, 0, 0.2)",
      padding: theme.spacing(0.5, 1),
      transition: "all 0.2s ease-in-out",
      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.04)",
        borderColor: "rgba(0, 0, 0, 0.3)",
      },
      "&.Mui-focused": {
        backgroundColor: "#fff",
        borderColor: clientColor,
        borderStyle: "solid",
        boxShadow: `0 0 0 2px ${clientColor}20`,
      },
    },
    "& input": {
      textAlign: "center",
      fontWeight: 500,
      fontSize: "1rem",
      cursor: "text",
      "&::placeholder": {
        color: grayColor[6],
        opacity: 0.7,
        fontStyle: "italic",
      },
    },
  },
  editIcon: {
    position: "absolute",
    left: theme.spacing(1),
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "1rem",
    color: "rgba(0, 0, 0, 0.4)",
    pointerEvents: "none",
    transition: "color 0.2s ease-in-out",
    "$editableVariantName:hover &": {
      color: "rgba(0, 0, 0, 0.6)",
    },
    "$editableVariantName .Mui-focused &": {
      color: clientColor,
    },
  },
  sheetSelector: {
    marginTop: theme.spacing(1),
  },
  deleteButton: {
    position: "absolute",
    top: theme.spacing(0.5),
    right: theme.spacing(0.5),
    width: theme.spacing(2.5),
    height: theme.spacing(2.5),
    padding: theme.spacing(0.25),
    color: dangerColor[0],
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    borderRadius: "50%",
    border: `1px solid ${dangerColor[0]}`,
    transition: "all 0.2s ease-in-out",
    zIndex: 1, // Ensure it's on top
    "&:hover": {
      backgroundColor: dangerColor[0],
      color: "#fff",
      transform: "scale(1.1)",
    },
    "& .MuiSvgIcon-root": {
      fontSize: "1rem",
    },
  },
  undoButton: {
    position: "absolute",
    top: theme.spacing(0.5),
    right: theme.spacing(0.5),
    color: clientColor,
    zIndex: 1, // Ensure it's on top
  },
  addVariantCard: {
    border: `1px dashed ${grayColor[4]}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    textAlign: "center",
    width: 180,
    height: 120,
    minWidth: 160,
    minHeight: 120,
    maxWidth: 180,
    maxHeight: 120,
    flex: "0 0 auto",
    position: "relative",
    backgroundColor: whiteColor,
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    "&:hover": {
      backgroundColor: grayColor[15],
      borderColor: grayColor[3],
    },
  },
  addVariantText: {
    fontSize: "0.875rem",
    fontWeight: 500,
    color: grayColor[1],
  },
  addIcon: {
    fontSize: "1.5rem",
    color: grayColor[1],
  },
});

const SectionVariantSelector = ({
  variants,
  variantSheetMapping,
  onSheetChange,
  onVariantCreate,
  onVariantDelete,
  onVariantUndoDelete,
  onVariantNameChange,
  variantsToDelete,
  readonly,
  fileTabs = [],
  invalidVariants = {},
}) => {
  console.log("VariantSelector received variants:", variants);
  const { userDetails } = useContext(AuthContext);
  const useStyles = makeStyles((theme) =>
    variantSelectorStyle(theme, userDetails.implementation_color),
  );
  const classes = useStyles();

  const T_PREFIX = "rgm.inputs.variant_editing";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  const handleStartCreation = () => {
    // Create temporary variant immediately in localVariants
    const tempId = `temp_${Date.now()}`;
    onVariantCreate(null, tempId); // Create empty variant directly in parent
  };

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      alignItems="flex-start"
      sx={{ gap: 3 }}
      style={{ maxWidth: "100%", overflow: "hidden" }}
    >
      {variants.map((variant) => {
        const isDeleted = variantsToDelete.includes(variant.variant_id);
        const isInvalid = invalidVariants[variant.variant_id];
        console.log(
          `Rendering variant ${variant.variant_id}: name=${variant.variant_name}, isInvalid=${isInvalid}`,
        );

        return (
          <Box
            key={variant.variant_id}
            className={`${classes.variantCard} ${isDeleted ? classes.deletedCard : ""} ${isInvalid && !readonly ? classes.invalidCard : ""}`}
          >
            {readonly ? (
              <Typography className={classes.variantName}>
                {variant.variant_name}
              </Typography>
            ) : (
              <Box className={classes.editableVariantName}>
                <TextField
                  value={variant.variant_name}
                  onChange={(e) =>
                    onVariantNameChange(variant.variant_id, e.target.value)
                  }
                  variant="standard"
                  placeholder={t("variant_name_placeholder")}
                  InputProps={{
                    disableUnderline: true,
                  }}
                  disabled={isDeleted}
                  fullWidth
                />
                {!isDeleted && <EditIcon className={classes.editIcon} />}
              </Box>
            )}

            {isDeleted ? (
              <Typography variant="body2" color="textSecondary">
                {t("marked_for_deletion")}
              </Typography>
            ) : (
              <FormControl
                fullWidth
                className={`${classes.sheetSelector} ${classes.selectFormControl}`}
              >
                <Select
                  value={variantSheetMapping[variant.variant_id] || ""}
                  onChange={(e) =>
                    onSheetChange(variant.variant_id, e.target.value)
                  }
                  disabled={readonly}
                  className={classes.select}
                  MenuProps={{
                    className: classes.selectMenu,
                    classes: {
                      paper: classes.selectPaper,
                    },
                  }}
                >
                  <MenuItem value="" className={classes.selectMenuItem}>
                    <em>-</em>
                  </MenuItem>
                  {fileTabs.map((tab) => (
                    <MenuItem
                      key={tab}
                      value={tab}
                      className={classes.selectMenuItem}
                    >
                      {tab}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {!readonly &&
              !(
                !isDeleted && variants.length - variantsToDelete.length === 1
              ) && (
                <Tooltip
                  title={
                    isDeleted
                      ? t("undo_delete_tooltip")
                      : t("delete_variant_tooltip")
                  }
                  className={classes.undoButton}
                  placement="bottom-start"
                >
                  <span>
                    <IconButton
                      size="small"
                      className={
                        isDeleted ? classes.undoButton : classes.deleteButton
                      }
                      onClick={() =>
                        isDeleted
                          ? onVariantUndoDelete(variant.variant_id)
                          : onVariantDelete(variant.variant_id)
                      }
                      disabled={
                        !isDeleted &&
                        variants.length - variantsToDelete.length === 1
                      }
                    >
                      {isDeleted ? <UndoIcon /> : <CloseIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
          </Box>
        );
      })}

      {/* Add Variant Button - looks like an idle variant */}
      {!readonly && (
        <Box className={classes.addVariantCard} onClick={handleStartCreation}>
          <AddIcon className={classes.addIcon} />
          <Typography className={classes.addVariantText}>
            {t("add_variant")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

SectionVariantSelector.propTypes = {
  variants: PropTypes.array.isRequired,
  variantSheetMapping: PropTypes.object.isRequired,
  onSheetChange: PropTypes.func.isRequired,
  onVariantCreate: PropTypes.func.isRequired,
  onVariantDelete: PropTypes.func.isRequired,
  onVariantUndoDelete: PropTypes.func.isRequired,
  onVariantNameChange: PropTypes.func.isRequired,
  variantsToDelete: PropTypes.array.isRequired,
  readonly: PropTypes.bool,
  fileTabs: PropTypes.array,
  invalidVariants: PropTypes.object,
};

export default SectionVariantSelector;
